import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../services/api";
import { useConfirm } from "../../hooks/useConfirm";
import RefreshButton from "../RefreshButton/RefreshButton";
import "./Ideas.css";

const STATUS_OPTIONS = ["open", "in_progress", "done", "archived"];
const PRIORITY_OPTIONS = ["low", "normal", "high"];

function Ideas() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterProject, setFilterProject] = useState("all");
  const [filterTag, setFilterTag] = useState("all");
  const [viewMode, setViewMode] = useState("active");
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [confirm, confirmDialog] = useConfirm();

  const fetchIdeas = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get("/ideas");
      setIdeas(data.items || []);
    } catch (err) {
      console.error("Failed to fetch ideas:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIdeas();
  }, [fetchIdeas]);

  const handleDelete = async (id) => {
    if (!(await confirm("Delete this idea?"))) return;
    try {
      await apiClient.delete(`/ideas/${id}`);
      fetchIdeas();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const projects = [...new Set(ideas.map((i) => i.project).filter(Boolean))].sort();
  const allTags = [...new Set(ideas.flatMap((i) => i.tags || []))].sort();

  const filtered = ideas.filter((i) => {
    if (viewMode === "active" && (i.status === "done" || i.status === "archived")) return false;
    if (viewMode === "done" && i.status !== "done") return false;
    if (viewMode === "archived" && i.status !== "archived") return false;
    if (filterStatus !== "all" && i.status !== filterStatus) return false;
    if (filterProject !== "all" && i.project !== filterProject) return false;
    if (filterTag !== "all" && !(i.tags || []).includes(filterTag)) return false;
    return true;
  });

  // Sort: done/archived items to bottom, then newest first by creation date
  const sorted = [...filtered].sort((a, b) => {
    const bottomStatuses = ["done", "archived"];
    const aBottom = bottomStatuses.includes(a.status) ? 1 : 0;
    const bBottom = bottomStatuses.includes(b.status) ? 1 : 0;
    if (aBottom !== bBottom) return aBottom - bBottom;
    return (b.createdAt || "").localeCompare(a.createdAt || "");
  });

  return (
    <div className="ideas-page">
      {confirmDialog}
      <div className="ideas-header">
        <h2>Ideas</h2>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <RefreshButton onClick={fetchIdeas} title="Refresh ideas" />
          <button className="btn-primary" onClick={handleAdd}>
            + New Idea
          </button>
        </div>
      </div>

      <div className="ideas-filters">
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">All Status</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s.replace("_", " ")}
            </option>
          ))}
        </select>
        <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)}>
          <option value="all">All Projects{projects.length > 0 ? ` (${projects.length})` : ""}</option>
          {projects.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        {allTags.length > 0 && (
          <select value={filterTag} onChange={(e) => setFilterTag(e.target.value)}>
            <option value="all">All Tags ({allTags.length})</option>
            {allTags.map((t) => (
              <option key={t} value={t}>#{t}</option>
            ))}
          </select>
        )}
        <button
          className={`btn-toggle ${viewMode === "done" ? "active" : ""}`}
          onClick={() => setViewMode(viewMode === "done" ? "active" : "done")}
        >
          {viewMode === "done" ? "✓ " : ""}Done ({ideas.filter((i) => i.status === "done").length})
        </button>
        <button
          className={`btn-toggle ${viewMode === "archived" ? "active" : ""}`}
          onClick={() => setViewMode(viewMode === "archived" ? "active" : "archived")}
        >
          {viewMode === "archived" ? "✓ " : ""}Archived ({ideas.filter((i) => i.status === "archived").length})
        </button>
        <span style={{ fontSize: "0.825rem", color: "var(--color-text-muted)", alignSelf: "center" }}>
          {sorted.length} idea{sorted.length !== 1 ? "s" : ""}
        </span>
      </div>

      {showForm && (
        <IdeaForm
          item={editItem}
          projects={projects}
          onSave={() => { setShowForm(false); fetchIdeas(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {loading ? (
        <div className="placeholder">Loading...</div>
      ) : sorted.length === 0 ? (
        <div className="placeholder">
          No ideas yet. Capture your next big idea!
        </div>
      ) : (
        <div className="ideas-list">
          {sorted.map((idea) => (
            <div key={idea.id} className={`idea-card ${idea.status}`}>
              <div className="idea-top">
                <span className="idea-title">{idea.title}</span>
                <div className="idea-badges">
                  <span className={`idea-badge status-${idea.status}`}>
                    {idea.status.replace("_", " ")}
                  </span>
                  {idea.priority && idea.priority !== "normal" && (
                    <span className={`idea-badge priority-${idea.priority}`}>
                      {idea.priority}
                    </span>
                  )}
                </div>
              </div>
              {idea.description && (
                <p className="idea-description">{idea.description}</p>
              )}
              <div className="idea-meta">
                {idea.project && <span className="idea-project">📁 {idea.project}</span>}
                {(idea.tags || []).map((tag) => (
                  <span key={tag} className="idea-tag">#{tag}</span>
                ))}
                {idea.source === "anna" && <span className="idea-source">💡 by Anna</span>}
                <div className="idea-actions">
                  <button className="btn-secondary btn-xs" onClick={() => handleEdit(idea)}>Edit</button>
                  <button className="btn-danger btn-xs" onClick={() => handleDelete(idea.id)}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IdeaForm({ item, projects, onSave, onCancel }) {
  const isEditing = Boolean(item);
  const [form, setForm] = useState({
    title: item?.title || "",
    description: item?.description || "",
    project: item?.project || "",
    status: item?.status || "open",
    priority: item?.priority || "normal",
    tags: item?.tags || [],
  });
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !form.tags.includes(tag)) {
      setForm({ ...form, tags: [...form.tags, tag] });
    }
    setTagInput("");
  };

  const removeTag = (tag) => {
    setForm({ ...form, tags: form.tags.filter((t) => t !== tag) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (isEditing) {
        await apiClient.put(`/ideas/${item.id}`, form);
      } else {
        await apiClient.post("/ideas", form);
      }
      onSave();
    } catch (err) {
      setError(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="idea-form">
      <h3>{isEditing ? "Edit" : "New"} Idea</h3>
      {error && <div className="form-error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Title</label>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g., Voice-controlled smart mirror with Anna"
            required
          />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Describe the idea in detail..."
            rows={4}
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Project</label>
            <input
              value={form.project}
              onChange={(e) => setForm({ ...form, project: e.target.value })}
              placeholder="e.g., Anna Project"
              list="project-suggestions"
            />
            <datalist id="project-suggestions">
              {projects.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
          </div>
          <div className="form-group">
            <label>Priority</label>
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
            >
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          {isEditing && (
            <div className="form-group">
              <label>Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s.replace("_", " ")}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="form-group">
          <label>Tags</label>
          <div className="tag-input-wrapper">
            <input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
              placeholder="Type a tag and press Enter"
            />
            <button type="button" className="btn-secondary btn-xs" onClick={addTag}>
              Add
            </button>
          </div>
          {form.tags.length > 0 && (
            <div className="tags-list">
              {form.tags.map((tag) => (
                <span key={tag} className="tag-chip">
                  #{tag}
                  <button type="button" onClick={() => removeTag(tag)}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Saving..." : isEditing ? "Update" : "Create"}
          </button>
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default Ideas;
